import React, { useCallback, useState } from 'react';
import { identity } from 'ramda';
import { useDispatch } from 'react-redux';
import { clsx } from 'js/utils/classNames.util';
import { useTranslations } from 'js/hooks/i18n';
import { useFormatter } from 'js/hooks/useFormatter';
import { ManagedPopup } from 'js/components/common/Popup';
import { WebContentInner } from 'js/components/system/WebContent';
import { useCombinedOnClose } from 'js/components/common/PopupManager';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';
import { getAcceptDeclineButtons } from 'js/components/common/popups/ConfirmationPopup';
import { updatePeriod as updateBonusHistoryPeriod } from 'js/components/main/BonusHistory/store/actions';

import { acceptBonus, declineBonus } from './server';
import { showFreeSpin2BonusGamesPopupAction } from './components/FreeSpin2BonusGamesPopup';

import style from './FreeSpin2BonusPopup.scss';

export const FreeSpin2BonusPopup = ({
    id,
    title,
    message,
    controls,
    dialogId,
    gameCodeNames,
    showGamesList,
    betConfigurations,
    onClose,
    layerName
}) => {
    const t = useTranslations('playtech.mobile.system.fs2.popup.');
    const formatter = useFormatter();
    const dispatch = useDispatch();
    const combinedOnClose = useCombinedOnClose(onClose, id);
    const [betConfigurationId, setBetConfigurationId] = useState(betConfigurations[0].configurationId);

    const showPopup = useCallback(({ selectedBetConfiguration }) => {
        dispatch(showFreeSpin2BonusGamesPopupAction({
            title,
            id: `${id}-games`,
            controls,
            layer: layerName,
            gameCodeNames,
            selectedBetConfiguration
        }));
    }, [
        title,
        controls,
        dispatch,
        id,
        layerName,
        gameCodeNames
    ]);

    const onAcceptClick = useCallback(async () => {
        await acceptBonus({
            bonusIdentifier: dialogId,
            freeBetConfigurationId: betConfigurationId
        });
        await combinedOnClose();

        dispatch(updateBonusHistoryPeriod());

        if (Number(showGamesList)) {
            const selectedBetConfiguration = betConfigurations
                .find((betConfiguration) => betConfiguration.configurationId === betConfigurationId);

                showPopup({ selectedBetConfiguration });
        }
    }, [betConfigurations, showGamesList, dialogId, combinedOnClose, betConfigurationId, showPopup, dispatch]);

    const onDeclineClick = useCallback(async () => {
        await declineBonus({ bonusIdentifier: dialogId });
        await combinedOnClose();

        dispatch(updateBonusHistoryPeriod());
    }, [dialogId, combinedOnClose, dispatch]);

    const buttons = getAcceptDeclineButtons({
        onAcceptClick,
        onDeclineClick
    });

    return (
        <ManagedPopup
            id={id}
            title={title}
            isModal={true}
            controls={controls}
            buttons={buttons}
            layerClassNames={style}
            onOverlayClick={identity}
        >
            <WebContentInner className={style.description} content={message} />
            {betConfigurations.length > 1 && (
                <div className={style.grid}>
                    {betConfigurations.map((betConfiguration) => (
                        <AccessibilityElement key={betConfiguration.configurationId}
                             onClick={() => setBetConfigurationId(betConfiguration.configurationId)}
                             className={clsx([
                                 style.selection,
                                 betConfiguration.configurationId === betConfigurationId ? style.activeSelection : null
                             ])}
                        >
                            <div className={style.initialCount}>{betConfiguration.initialCount}</div>
                            <div className={style.text}>{t('freeSpins', 'free spins')}</div>
                            <div className={style.x}>{t('x', 'x')}</div>
                            <div className={style.value}>
                                {formatter.formatCurrency(betConfiguration.betValue.amount, {
                                    currencyCode: betConfiguration.betValue.amount.currencyCode
                                })}
                            </div>
                        </AccessibilityElement>
                    ))}
                </div>
            )}
        </ManagedPopup>
    );
};
